import { useState, useEffect, useCallback } from "react";
import axios from "../customAxios";
import { dateToDatabase, dateToLocalDatabase } from "../utils/functions";


// Auxiliary function to initialize filters from localStorage, outside the hook.
const getInitialState = () => {
  const savedStateJSON = localStorage.getItem("driverDeliveriesFilter");
  const defaults = {
    filters: {
      assignedAt: new Date(),
      statusFilter: "all",
      search: "",
    },
  };

  if (savedStateJSON) {
    const savedState = JSON.parse(savedStateJSON);
    return {
      filters: {
        assignedAt: savedState.assignedAt ? new Date(savedState.assignedAt) : defaults.filters.assignedAt,
        statusFilter: savedState.statusFilter || defaults.filters.statusFilter,
        search: savedState.search || defaults.filters.search
      }
    };
  }
  return defaults;
};

export function useDriverDeliveries() {
  // CENTRALIZED STATE
  const [initialState] = useState(() => getInitialState());
  const [filters, setFilters] = useState(initialState.filters);
  const [auxSearcher, setAuxSearcher] = useState(filters.search);

  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // STATE MANNERS (HANDLERS)
  const handleFilterChange = useCallback((filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  }, []);

  const goToPreviousDay = useCallback(() => {
    setFilters((prev) => {
      const newDate = new Date(prev.assignedAt);
      newDate.setDate(newDate.getDate() - 1);
      return { ...prev, assignedAt: newDate };
    });
  }, []);

  const goToNextDay = useCallback(() => {
    setFilters((prev) => {
      const newDate = new Date(prev.assignedAt);
      newDate.setDate(newDate.getDate() + 1);
      return { ...prev, assignedAt: newDate };
    });
  }, []);

  const goToToday = useCallback(() => {
    handleFilterChange("assignedAt", new Date());
  }, [handleFilterChange]);

  const fetchDeliveries = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = {
      assigned_at: dateToLocalDatabase(filters.assignedAt),
      search: filters.search.trim() !== "" ? filters.search.trim() : null,
    };

    try {
      const { data } = await axios.get("deliveries/get_driver_deliveries/", {
        params,
      });
      setDeliveries(data);
    } catch (error) {
      console.error(`Error al obtener las entregas: ${error}`);
      setError("No se pudieron cargar las rutas");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const startPendingRoutes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const pendingIds = deliveries
        .filter((delivery) =>  delivery.status === "assigned" || delivery.status === "pending"
        || delivery.status === "withdrawn" || delivery.status === "in_cd" || delivery.status === "created_on_courier"
        || delivery.status === "fulfilled" || delivery.status === "ready_for_pickup"
        || delivery.status === "dropped_at_hub" || delivery.status === "on_the_way")
        .map((delivery) => delivery.id);

      if (pendingIds.length === 0) {
        setLoading(false);
        return;
      }

      await axios.post("deliveries/start_driver_deliveries_route/", {
        ids: pendingIds,
      });
      const updatedDeliveries = deliveries.map((delivery) => {
        if (pendingIds.includes(delivery.id)) {
          return { ...delivery, status: "on_the_way" };
        }
        return delivery;
      });
      setDeliveries(updatedDeliveries);
    } catch (error) {
      console.error(`Error al iniciar las rutas: ${error}`);
      setError("No se pudieron iniciar las rutas");
    } finally {
      setLoading(false);
    }
  }, [deliveries, setDeliveries, setLoading, setError]);

  // SECONDARY EFFECTS (FETCH, DEBUNCE, LOCALSTORAGE)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (auxSearcher !== filters.search) {
        handleFilterChange("search", auxSearcher);
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [auxSearcher, filters.search, handleFilterChange]);

  useEffect(() => {
    localStorage.setItem(
      "driverDeliveriesFilter",
      JSON.stringify(filters)
    );
  }, [filters]);


  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  return {
    deliveries,
    loading,
    error,
    filters,
    auxSearcher,
    startPendingRoutes,
    refetch: () => fetchDeliveries(),
    selectedDate: filters.assignedAt,
    goToPreviousDay,
    goToNextDay,
    goToToday,
    setAuxSearcher
  };
}
